// Escalation-step delays.
//
// A step is stored as a single integer number of seconds. The editor shows it
// as a number plus a unit, so "30 sec" and "2 days" are the same field — one
// stored unit with the magnitude chosen at the edge, the same arrangement
// tenant_post_delivery_feedback.delay_minutes uses. A stored unit column
// could disagree with the number beside it; a derived one cannot.

export const UNITS = [
  { value: "s", label: "sec", seconds: 1 },
  { value: "m", label: "min", seconds: 60 },
  { value: "h", label: "hours", seconds: 3600 },
  { value: "d", label: "days", seconds: 86400 },
];

// Matches MAX_RUNG_SECONDS in apps/api/server.py.
export const MAX_RUNG_SECONDS = 90 * 24 * 60 * 60;

export const unitSeconds = (unit) =>
  (UNITS.find((u) => u.value === unit) || UNITS[1]).seconds;

/**
 * Split a stored delay into the largest unit that divides it exactly, so a
 * rung saved as 7200 comes back as "2 hours" rather than "120 min".
 *
 * 0 divides by everything, so it would otherwise read as "0 days"; it is
 * shown in minutes, which sits most naturally beside its neighbours.
 */
export function splitDelay(seconds) {
  const total = Number(seconds) || 0;
  if (total <= 0) return { amount: 0, unit: "m" };
  for (let i = UNITS.length - 1; i >= 0; i -= 1) {
    const u = UNITS[i];
    if (total >= u.seconds && total % u.seconds === 0) {
      return { amount: total / u.seconds, unit: u.value };
    }
  }
  return { amount: total, unit: "s" };
}

/** The stored value for a rung being edited. */
export const toSeconds = (rung) =>
  Math.round(Number(rung.amount || 0) * unitSeconds(rung.unit));

/**
 * Why this ladder cannot be saved, or "" if it can.
 *
 * Mirrors put_sla_ladder's own checks so the editor says what is wrong before
 * a round trip, not instead of one — the server still rejects a bad payload.
 */
export function ladderProblem(rungs) {
  const amounts = rungs.map((r) => Number(r.amount));
  if (amounts.some((a) => !Number.isFinite(a) || a < 0)) {
    return "A step can't wait a negative amount of time.";
  }
  const delays = rungs.map(toSeconds);
  if (delays.some((d) => d > MAX_RUNG_SECONDS)) {
    return "A step can't wait more than 90 days.";
  }
  // Two steps at the same delay would fire in an arbitrary order.
  if (new Set(delays).size !== delays.length) {
    return "Two steps can't be set to the same delay.";
  }
  if (rungs.some((r) => r.enabled && !(r.template_name || "").trim())) {
    return "Every active step needs a template name.";
  }
  return "";
}

// The order of a ladder is the order its steps are drawn in, never the times typed. Each step's delay is
// how long it waits after the step before it (the first, after the start), and the stored delay, which
// is counted from the start, is those waits added up. So changing one wait can only move the steps
// after it; it never reorders anything.

// A step that waits for a "no" has no timer of its own: it goes out the moment the answer comes, so it
// sits one second after the step before it (the stored delays must differ, and this one is never used to wait).
export const GATED_OFFSET_SECONDS = 1;

/** Stored delays (counted from the start) for steps in drawn order. `gated` holds the keys of waiting steps. */
export function absoluteSeconds(rungs, gated = new Set()) {
  let at = 0;
  return rungs.map((r) => {
    at += gated.has(r.key) ? GATED_OFFSET_SECONDS : toSeconds(r);
    return at;
  });
}

/**
 * The editor's steps from stored ones: put in stored order, each given the wait after the step before it.
 * A step after the first always waits at least a second, and a waiting step is the fixed second.
 * `isGated(sorted)` returns the keys of the waiting steps in that order.
 */
export function withWaits(items, isGated = () => new Set()) {
  const sorted = [...items].sort((a, b) => Number(a.seconds) - Number(b.seconds));
  const gated = isGated(sorted);
  let before = 0;
  return sorted.map((r, i) => {
    const wait = gated.has(r.key) ? GATED_OFFSET_SECONDS : Math.max((Number(r.seconds) || 0) - before, i === 0 ? 0 : 1);
    before += wait;
    return { ...r, ...splitDelay(wait) };
  });
}

/** Why the waits cannot be saved, or "" if they can. */
export function waitProblem(rungs, gated = new Set()) {
  if (rungs.some((r) => !Number.isFinite(Number(r.amount)) || Number(r.amount) < 0)) {
    return "A step can't wait a negative amount of time.";
  }
  if (rungs.some((r, i) => i > 0 && !gated.has(r.key) && toSeconds(r) <= 0)) {
    return "Every step after the first has to wait at least a second after the step before it.";
  }
  return "";
}
